import React from 'react';
import Map, {MapRef, PaddingOptions, Source} from 'react-map-gl';
import {Spin} from 'antd';

import ErrorBoundary from '../../../../components/ErrorBoundary';
import BookingMapDrawer from './BookingMapDrawer';
import BookingMapLayers from './BookingMapLayers';
import {useGetDeploymentsIncludeAvailabilityQuery} from '../../../../store/api/deploymentsApi';
import availableClusterIconEngage from '../../../../assets/map/markers/engage-availableCluster.png';
import availableMarkerIconEngage from '../../../../assets/map/markers/engage-availableMarker.png';
import mixedClusterIconEngage from '../../../../assets/map/markers/engage-mixedCluster.png';
import selectedClusterIconEngage from '../../../../assets/map/markers/engage-selectedCluster.png';
import selectedMarkerIconEngage from '../../../../assets/map/markers/engage-selectedMarker.png';
import selectedMixedClusterIconEngage from '../../../../assets/map/markers/engage-selectedMixedCluster.png';
import unavailableClusterIconEngage from '../../../../assets/map/markers/engage-unavailableCluster.png';
import unavailableMarkerIconEngage from '../../../../assets/map/markers/engage-unavailableMarker.png';
import {convertListToGeoJson} from '../../../../helpers/map';
import {
    BookingMapMarkerInfo,
    BookingMapProps,
    CarMapDeployable,
} from '../../../../types/deploymentTypes';
import {MapMouseEvent, MapTouchEvent} from 'mapbox-gl';

import 'mapbox-gl/dist/mapbox-gl.css';

const mapStyle = 'mapbox://styles/modo-coop/ckzj0nh70001k16ot4gclxsrs';
const accessToken = window.env.MAPBOX_TOKEN;

const csoLatitude = parseFloat(window.env.MAP_DEFAULT_LAT_LONG.split(',')[0]);
const csoLongitude = parseFloat(window.env.MAP_DEFAULT_LAT_LONG.split(',')[1]);
const initialMapPadding = {top: 0, right: 0, bottom: 0, left: 0};
const viewState = {
    latitude: csoLatitude,
    longitude: csoLongitude,
    zoom: 10,
};

const BookingMap = ({startedAt, endedAt, locationInput, markerState, onClickBack, setOnClickBack}: BookingMapProps) => {
    const mapRef = React.useRef<MapRef | null>(null);
    const mobileMode = window.innerWidth <= 575;

    const {data: locationCarsList = {}, isLoading: deploymentsLoading} = useGetDeploymentsIncludeAvailabilityQuery({
        startedAt,
        endedAt
    });

    const [drawerIsOpen, setDrawerIsOpen] = React.useState<boolean>(false);
    const [markerInfo, setMarkerInfo] = React.useState<BookingMapMarkerInfo>({
        id: 0,
        properties: {
            location: {
                location_id: 0,
                name: '',
                description: ''
            }
        }
    });
    const [mapCenter, setMapCenter] = React.useState<[number, number]>([csoLongitude, csoLatitude]);
    const [mapPadding, setMapPadding] = React.useState<PaddingOptions>(initialMapPadding);

    //region onMapLoad

    const onMapLoad = React.useCallback(() => {
        const mapCurrent = mapRef.current;

        const imageDetails = [
            {name: 'engage-availableCluster', src: availableClusterIconEngage, width: 32, height: 32},
            {name: 'engage-availableMarker', src: availableMarkerIconEngage, width: 35, height: 45},
            {name: 'engage-selectedMarker', src: selectedMarkerIconEngage, width: 35, height: 45},
            {name: 'engage-selectedCluster', src: selectedClusterIconEngage, width: 32, height: 32},
            {name: 'engage-unavailableMarker', src: unavailableMarkerIconEngage, width: 35, height: 45},
            {name: 'engage-unavailableCluster', src: unavailableClusterIconEngage, width: 32, height: 32},
            {name: 'engage-mixedCluster', src: mixedClusterIconEngage, width: 32, height: 32},
            {name: 'engage-selectedMixedCluster', src: selectedMixedClusterIconEngage, width: 32, height: 32}
        ];

        if (mapCurrent !== null) {
            const map = mapCurrent.getMap();
            const imagesToAdd = imageDetails.filter(({name}) => !map.hasImage(name));

            imagesToAdd.forEach(({name, src, width, height}) => {
                const img = new Image(width, height);
                img.onload = () => map.addImage(name, img);
                img.src = src;
            });
        }
    }, []);

    // Our geojson feature data, memoized to avoid reconstructing the map.
    const geoJson = React.useMemo(() => {
        return convertListToGeoJson(locationCarsList);
    }, [locationCarsList]);

    //endregion

    //region Map hooks

    const centerMap = React.useCallback((mapCurrent: MapRef | null, resetPadding: boolean = false) => {
        if (mapCurrent === null) return;

        // When a start/end/location input are filled, it triggers the React.useEffect to recenter the map to the previous
        //  mapCenter and mapPadding state. Eventually the locationInput will center the map to the searched location,
        //  so this is a temp solution so the map doesn't center to the previous state on input change.
        // To allow for map recentering to occur onCloseDrawer while start/end input is filled, the resetPadding check is required.

        let preventCenter = false;
        if (startedAt || endedAt || locationInput) preventCenter = true;
        if (drawerIsOpen) preventCenter = false;

        if (resetPadding || !preventCenter) {
            const newPadding = resetPadding ? initialMapPadding : mapPadding;
            const map = mapCurrent.getMap();
            map.easeTo({
                center: mapCenter,
                padding: newPadding,
            });
        }
    }, [drawerIsOpen, endedAt, locationInput, mapCenter, mapPadding, startedAt]);

    // Watches for mobile breakpoint to adjust map padding/centering.
    React.useEffect(() => {
        if (drawerIsOpen) {
            setMapPadding(prevState => ({
                ...prevState,
                ...(mobileMode ? {bottom: 345, right: 0} : {bottom: 0, right: 450}),
            }));
        }
    }, [drawerIsOpen, mobileMode]);

    // Centralize the map and update marker info.
    React.useEffect(() => {
        const mapCurrent = mapRef.current;

        centerMap(mapCurrent, false);

        if (markerInfo.id !== null && drawerIsOpen) {
            // If start/end have been updated, use the present markerInfo.id to search for the current
            //  map feature which will contain the up-to-date availability statuses of the selected marker.
            const feature: GeoJSON.Feature | undefined = geoJson.features.find((feature: GeoJSON.Feature) => feature.id === markerInfo.id);

            if (feature && feature.id && mapCurrent) {
                setMarkerInfo({
                    id: Number(feature.id),
                    properties: {
                        ...(feature.properties || {}),
                        deployments: (feature.properties && feature.properties.deployments) ?
                            [...feature.properties.deployments].sort((a: CarMapDeployable, b: CarMapDeployable) => {
                                const availabilityA = a && a.car_is_available ? 1 : 0;
                                const availabilityB = b && b.car_is_available ? 1 : 0;
                                return availabilityB - availabilityA;
                            }) : [],
                        location: (feature.properties && feature.properties.location) ? feature.properties.location : {},
                        coordinates: (feature.geometry && feature.geometry.type === 'Point') ? [feature.geometry.coordinates[0], feature.geometry.coordinates[1]] : [0, 0],
                        zoomLevel: mapCurrent.getZoom(),
                    },
                });
            }
        }
    }, [mapCenter, mapPadding, drawerIsOpen, geoJson, markerInfo.id, centerMap]);

    // Retains state from BookingMapCreate/VehicleInfo screen when clicking back to booking map.
    React.useEffect(() => {
        if (onClickBack && mapRef.current) {
            const map = mapRef.current.getMap();
            onMapLoad();
            map.setZoom(markerState.properties.zoomLevel ?? 0);

            setDrawerIsOpen(true);
            setMarkerInfo(markerState);
            setMapCenter(markerState.properties.coordinates ?? [0, 0]);
            setMapPadding((prevState) => ({
                ...prevState,
                ...(mobileMode ? {bottom: 345, right: 0} : {bottom: 0, right: 450}),
            }));
            setOnClickBack(false);
        }
    }, [onClickBack, markerState, setOnClickBack, onMapLoad, mobileMode]);

    //endregion

    //region Event Handlers

    const onCloseDrawer = () => {
        setMapPadding(initialMapPadding);
        setDrawerIsOpen(!drawerIsOpen);
        setMapCenter(markerInfo.properties.coordinates ?? [0, 0]);
        centerMap(mapRef.current, true);
    };

    const onClick = (e: MapMouseEvent | MapTouchEvent) => {
        const map = mapRef.current?.getMap();

        // When click on a marker get the feature id.
        const features = map?.queryRenderedFeatures(e.point, {
            layers: ['engage-availableMarker', 'engage-availableCluster',
                'engage-unavailableMarker', 'engage-unavailableCluster', 'engage-mixedCluster']
        });

        if (
            features &&
            features.length &&
            features[0] &&
            features[0].properties &&
            features[0].geometry &&
            features[0].geometry.type === 'Point' &&
            features[0].geometry.coordinates &&
            features[0].geometry.coordinates.length >= 2
        ) {
            setMarkerInfo({
                id: Number(features[0].id),
                properties: {
                    ...features[0].properties,
                    // Pushes unavailable deployments to end of the list.
                    deployments: JSON.parse(features[0].properties.deployments).sort((a: CarMapDeployable, b: CarMapDeployable) => {
                        const availabilityA = a.car_is_available ? 1 : 0;
                        const availabilityB = b.car_is_available ? 1 : 0;
                        return availabilityB - availabilityA;
                    }),
                    location: JSON.parse(features[0].properties.location),
                    coordinates: [features[0].geometry.coordinates[0], features[0].geometry.coordinates[1]],
                    zoomLevel: map?.getZoom() || 0,
                },
            });
            setDrawerIsOpen(true);

            // When drawer is open set map padding for the map view area and map center as the coordinates of a clicked point.
            setMapCenter([features[0].geometry.coordinates[0], features[0].geometry.coordinates[1]]);
            setMapPadding((prevState) => ({
                ...prevState,
                ...(mobileMode ? {bottom: 345, right: 0} : {bottom: 0, right: 450}),
            }));
        }
    };

    //endregion

    return (
        <>
            <Spin spinning={deploymentsLoading}/>
            <ErrorBoundary>
                <Map
                    initialViewState={viewState}
                    style={{width: '100%', height: '100%'}}
                    mapboxAccessToken={accessToken}
                    ref={mapRef}
                    onClick={onClick}
                    mapStyle={mapStyle}
                    dragRotate={false}
                    onLoad={onMapLoad}
                >
                    {
                        geoJson.features.length &&
                        <Source
                            id="car-map"
                            type="geojson"
                            data={geoJson}
                            cluster={false}
                        >
                            {/* BookingMapLayers is responsible for the marker icons appearing on the map */}
                            <BookingMapLayers markerInfo={markerInfo.id}/>
                        </Source>
                    }
                    {
                        Object.keys(markerInfo).length &&
                        <BookingMapDrawer
                            bookingMapInputState={{startedAt, endedAt, locationInput}}
                            markerInfo={markerInfo}
                            drawerIsOpen={drawerIsOpen}
                            onCloseDrawer={onCloseDrawer}
                            loading={deploymentsLoading}
                        />
                    }
                </Map>
            </ErrorBoundary>
        </>
    );
}

export default BookingMap;
